import { FormHelperText, Stack } from '@mui/material';

import { FHButton, FProgressBar } from '../../../../../../components/base';
import { FBoxContainer } from '../../../../../../components/baseV2';
import { Themes } from '../../../../../../constants';
import { ConfirmDialog, LIST_CONTAINER_SX, useFormReadOnly } from '../../../../../common';
import { DISCHARGE_REASON_OTHER } from '../constants';
import { useDischargeReasonDraft } from '../hooks';
import {
  DISCHARGE_ADD_REASON_BUTTON_SX,
  DISCHARGE_REASONS_CARD_DRAFTING_SX,
  DISCHARGE_REASONS_CARD_SX,
} from '../styles';
import { getDischargeReasonRowLabel } from '../utils';
import DischargeReasonDraftFields from './DischargeReasonDraftFields';
import DischargeReasonRow from './DischargeReasonRow';

import type { DischargeReasonsSectionProps } from './types';

export default function DischargeReasonsSection({
  reasons,
  disabled,
  isSaving,
  onCreate,
  onDelete,
}: DischargeReasonsSectionProps) {
  const readOnly = useFormReadOnly();
  const reasonDraft = useDischargeReasonDraft({ reasons, onCreate, onDelete });

  return (
    <FBoxContainer
      sx={[
        LIST_CONTAINER_SX,
        DISCHARGE_REASONS_CARD_SX,
        !readOnly && reasonDraft.draft.reason !== '' && DISCHARGE_REASONS_CARD_DRAFTING_SX,
      ]}
    >
      {isSaving ? <FProgressBar /> : null}

      {readOnly ? null : (
        <>
          <DischargeReasonDraftFields
            disabled={disabled}
            draft={reasonDraft.draft}
            explanationError={reasonDraft.draftError}
            onExplanationChange={reasonDraft.setExplanation}
            onReasonChange={reasonDraft.setReason}
            options={reasonDraft.availableOptions}
          />
          <Stack
            direction="row"
            justifyContent="flex-end"
            mt={reasonDraft.draft.reason === DISCHARGE_REASON_OTHER ? 0.5 : 1.25}
          >
            <FHButton
              colorTheme={Themes.Primary}
              disabled={disabled || isSaving || !reasonDraft.canAdd}
              onClick={reasonDraft.submit}
              sx={DISCHARGE_ADD_REASON_BUTTON_SX}
            >
              Add reason
            </FHButton>
          </Stack>
        </>
      )}

      {reasons.length > 0 ? (
        <Stack mt={readOnly ? 0 : 0.5}>
          {reasons.map((reason, index) => (
            <DischargeReasonRow
              key={reason.id}
              disabled={disabled || isSaving}
              index={index}
              onDelete={reasonDraft.requestDelete}
              reason={reason}
              showDelete={!readOnly}
            />
          ))}
        </Stack>
      ) : null}
      {reasons.length === 0 ? (
        <FormHelperText error>Add at least one reason.</FormHelperText>
      ) : null}

      <ConfirmDialog
        confirmLoading={isSaving}
        message={
          reasonDraft.pendingDeleteReason === null
            ? ''
            : `You are about to delete "${getDischargeReasonRowLabel(reasonDraft.pendingDeleteReason)}"`
        }
        onClose={reasonDraft.cancelDelete}
        onConfirm={reasonDraft.confirmDelete}
        open={reasonDraft.pendingDeleteReason !== null}
        title="Confirm reason deletion?"
      />
    </FBoxContainer>
  );
}
